import React, { useState } from "react";
import { Container, Button, Alert } from "react-bootstrap";
import {
  MapPin,
  Clock,
  User,
  Phone,
  Mail,
  Send,
  CheckCircle,
} from "lucide-react";
import HeroSection from "./Components/HeroSection";
import CustomFormInput from "../modules/apps/components/CustomFormInput";
import { envConfig } from "../../utils/config";
import { ContactUsAPI } from "./core/request";
import { useSnackbar } from "../../_theme/context/SnackbarContext";
import { LogError } from "../../utils/GlobalLogging";
import VideoWithFallback from "./Components/VideoWithFallback";
import { getApiErrorMessage } from "../../utils/utils";

// Type definitions
interface ContactInfo {
  id: number;
  icon: React.ReactNode;
  title: string;
  content: React.ReactNode;
}

interface FormData {
  name: string;
  email: string;
  message: string;
}

interface FormErrors {
  name: string;
  email: string;
}

// Constants
const CONTACT_INFO: ContactInfo[] = [
  {
    id: 1,
    icon: <MapPin size={20} className="me-2" />,
    title: "Address",
    content: (
      <>
        9 Wale St,
        <br />
        Cape Town City Centre,
        <br />
        Cape Town,
        <br />
        8001, South Africa
      </>
    ),
  },
  {
    id: 2,
    icon: <Clock size={20} className="me-2" />,
    title: "Working Days",
    content: (
      <>
        Monday – Friday: 8am – 5pm
        <br />
        Weekends – 8am – 1pm
        <br />
        Public Holidays – <strong>Closed</strong>
      </>
    ),
  },
  {
    id: 3,
    icon: <User size={20} className="me-2" />,
    title: "Contact",
    content: "Ms. Labeeqah Schuurman",
  },
  {
    id: 4,
    icon: <Phone size={20} className="me-2" />,
    title: "Phone",
    content: (
      <>
        <p className="mb-1">
          <a href="tel:+27214832955" className="text-dark text-decoration-none">
            +27 (021) 483 2955
          </a>
        </p>
        <p className="mb-0">
          <a href="tel:+27824947990" className="text-dark text-decoration-none">
            (0)82 494 7990
          </a>
        </p>
      </>
    ),
  },
  {
    id: 5,
    icon: <Mail size={20} className="me-2" />,
    title: "Email",
    content: (
      <a
        href="mailto:Labeeqah.Schuurman@westerncape.gov.za"
        className="text-dark text-decoration-none"
      >
        Labeeqah.Schuurman@westerncape.gov.za
      </a>
    ),
  },
];

// Contact Info Component
const ContactInfoSection: React.FC = () => (
  <div className="col-12 col-xl-6 contact-info text-center">
    {CONTACT_INFO.map((info) => (
      <div key={info.id} className="my-5">
        <h5 className="mb-3">
          {info.icon}
          {info.title}
        </h5>
        <div className="mb-5">
          {typeof info.content === "string" ? (
            <p className="mb-0 text-break">{info.content}</p>
          ) : (
            info.content
          )}
        </div>
      </div>
    ))}
  </div>
);

// Contact Form Component
const ContactForm: React.FC = () => {
  const { showSnackbar } = useSnackbar();
  const [formData, setFormData] = useState<FormData>({
    name: "",
    email: "",
    message: "",
  });

  const [errors, setErrors] = useState<FormErrors>({
    name: "",
    email: "",
  });

  const [showSuccess, setShowSuccess] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>("");

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const { name, value } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Clear error for the field being edited
    setErrors((prev) => ({
      ...prev,
      [name]: "",
    }));

    // Clear any previous error message
    if (errorMessage) {
      setErrorMessage("");
    }
  };

  const validate = (): boolean => {
    const newErrors: FormErrors = {
      name: "",
      email: "",
    };

    let isValid = true;

    if (!formData.name.trim()) {
      newErrors.name = "Name is required";
      isValid = false;
    }

    if (!formData.email.trim()) {
      newErrors.email = "Email is required";
      isValid = false;
    } else if (
      !/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(formData.email)
    ) {
      newErrors.email = "Please enter a valid email address";
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validate()) return;

    setLoading(true);

    try {
      const contactData = {
        name: formData.name.trim(),
        email: formData.email.trim(),
        message: formData.message.trim(),
      };

      const response = await ContactUsAPI(contactData);

      if (response.status === "ok") {
        showSnackbar(response.data.message, "success");
        setShowSuccess(true);
        setFormData({
          name: "",
          email: "",
          message: "",
        });
      } else {
        showSnackbar(
          response.message || "Failed to send message. Please try again.",
          "danger"
        );
      }
    } catch (error: unknown) {
      showSnackbar(
        getApiErrorMessage(error, "Failed to send message. Please try again."),
        "danger"
      );
      LogError("Failed to send message", error, "ContactUs");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="md-12 col-xl-6 contact-form-card py-6">
      <h5 className="fw-bold mb-3">Send us a message</h5>

      <p className="mb-4">
        If you have any queries, feel free to contact us using the form below.
      </p>

      {showSuccess && (
        <Alert variant="success" className="d-flex align-items-center">
          <CheckCircle size={20} className="me-2" />
          Thank you! Your message has been sent successfully.
        </Alert>
      )}

      {errorMessage && (
        <Alert variant="danger" className="d-flex align-items-center">
          <span className="me-2">⚠️</span>
          {errorMessage}
        </Alert>
      )}

      <form onSubmit={handleSubmit}>
        <div className="mb-3">
          <CustomFormInput
            type="text"
            name="name"
            placeholder="Enter your name"
            value={formData.name}
            onChange={handleChange}
            disabled={loading}
          />

          {errors.name && (
            <small className="text-danger">{errors.name}</small>
          )}
        </div>

        <div className="mb-3">
          <CustomFormInput
            name="email"
            placeholder="Enter your email"
            value={formData.email}
            onChange={handleChange}
            disabled={loading}
          />

          {errors.email && (
            <small className="text-danger">{errors.email}</small>
          )}
        </div>

        <div className="mb-4">
          <CustomFormInput
            as="textarea"
            name="message"
            rows={5}
            placeholder="Enter your message (Optional)"
            value={formData.message}
            onChange={handleChange}
            disabled={loading}
          />
        </div>

        <Button
          type="submit"
          variant="primary"
          className="btn-send px-4"
          disabled={loading}
        >
          {loading ? (
            <>
              <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
              Sending...
            </>
          ) : (
            <>
              <Send size={18} className="me-2" />
              Send Now
            </>
          )}
        </Button>
      </form>
    </div>
  );
};

// Maps Section Component
const MapsSection: React.FC = () => (
  <section className="py-5 bg-white">
    <Container>
      <div>
        <div className="col-12 px-10 px-sm-15 px-md-10 px-lg-20 px-xl-20">
          <iframe
            src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3310.659599838733!2d18.4185677!3d-33.92415950000001!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x1dcc6765aae546ed%3A0xbb713b42f5e9075a!2s9%20Wale%20St%2C%20Cape%20Town%20City%20Centre%2C%20Cape%20Town%2C%208001%2C%20South%20Africa!5e0!3m2!1sen!2sin!4v1753877788772!5m2!1sen!2sin"
            width="100%"
            height="450"
            style={{ border: 0 }}
            allowFullScreen
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
            title="Google Maps - Department of Infrastructure Location"
          />
        </div>
      </div>
    </Container>
  </section>
);

// Main Contact Us Component
const ContactUs: React.FC = () => {

  return (
    <div className="overflow-y-auto overflow-x-hidden about-us">
      <HeroSection
        videoSrc={`${envConfig.APP_MEDIA_URL}videos/public-pages/Contactus1.mp4`}
        title="Department Of Infrastructure"
        subtitle="Contact us"
        fallbackImage={`${envConfig.APP_MEDIA_URL}images/public-pages/Contactus1.webp`}
        scrollParentId="introduction-section"
      />

      <section className="bg-white" id="introduction-section">
        <div className="row  d-flex flex-column-reverse flex-lg-row section-padding g-10 bg-white align-items-center m-0">
          <div className="col-12 col-md-6 col-xl-8 d-flex align-items-center justify-content-center left-side-content px-10 px-sm-15 px-md-10 px-lg-20 px-xl-20 pe-lg-5 pe-xl-5">
            <div className="bg-white shadow rounded-4 p-4 py-6">
              <div className="row">
                <ContactInfoSection />
                <ContactForm />
              </div>
            </div>
          </div>
          <div className="col-12 col-md-6 col-xl-4 d-flex align-items-center px-10 px-sm-15 px-md-10 px-lg-20 px-xl-20 ps-lg-5 ps-xl-5 mt-10 mt-lg-0 ">
            <div className="rounded-4 overflow-hidden h-100 w-100">
              <VideoWithFallback
                videoSrc={`${envConfig.APP_MEDIA_URL}videos/public-pages/Contactus2.mp4`}
                fallbackSrc={`${envConfig.APP_MEDIA_URL}images/public-pages/Contactus2.webp`}
                alt="Contact"
                videoClassName="h-100 w-100"
              />
            </div>
          </div>
        </div>
      </section>

      <MapsSection />
    </div>
  );
};

export default ContactUs;